import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  MediaDetails,
  UpsertLabelsParameters,
  Label,
  LabelReviewStatus,
  MediaStatusType,
  SelectMediaOption,
} from '@clef/shared/types';

import LabelAPI from '@/api/label_api';
import { useGetSelectedProjectQuery } from '@/serverStore/projects';
import { useTypedSelector } from '@/hooks/useTypedSelector';
import { useCurrentProjectModelInfoQuery } from '@/serverStore/projectModels';
import { datasetQueryKeys } from '@/serverStore/dataset';
import { labelQueryKeys } from '@/serverStore/label';
import { generateRandomId } from '@/utils';

export const useUpsertLabels = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { datasetId, id: projectId } = useGetSelectedProjectQuery().data ?? {};
  const { id: currentModelId } = useCurrentProjectModelInfoQuery();
  const { id: currentUserId } = useTypedSelector(state => state.login.user!);

  return useMutation({
    mutationFn: async (params: UpsertLabelsParameters) => {
      if (!projectId || !datasetId) return;
      const { annotations, mediaId, mediaLevelLabel, source } = params;
      await LabelAPI.upsertLabels({
        projectId,
        mediaId,
        annotations,
        mediaLevelLabel,
        source,
        modelId: currentModelId,
      });
      return params;
    },
    onMutate: params => {
      if (!datasetId) return;
      const { annotations, mediaId, mediaLevelLabel, source } = params;
      const mediaDetailQueryKey = datasetQueryKeys.mediaDetails(datasetId, {
        mediaId,
        modelId: currentModelId,
      });
      queryClient.cancelQueries(mediaDetailQueryKey);
      const prevData = queryClient.getQueriesData(mediaDetailQueryKey);

      queryClient.setQueryData<MediaDetails>(
        datasetQueryKeys.mediaDetails(datasetId, {
          mediaId,
          modelId: currentModelId,
        }),
        prev => {
          if (!prev) return prev;
          const newLabel = prev.label ? { ...prev.label } : ({ id: Date.now() } as Label);

          newLabel.reviewStatus = LabelReviewStatus.Accepted;
          newLabel.labelerName = currentUserId;
          newLabel.annotations = annotations.map(ann => ({ ...ann, id: generateRandomId() })); // for optimistic update data used in canvas
          newLabel.mediaLevelLabel = mediaLevelLabel;
          newLabel.source = source;
          return {
            ...prev,
            label: newLabel,
            mediaStatus:
              annotations.length === 0 && !mediaLevelLabel
                ? MediaStatusType.Raw
                : MediaStatusType.Approved,
          } as MediaDetails;
        },
      );
      return { params, prevData };
    },
    onError: (error: Error, _, context) => {
      if (!datasetId) return;
      queryClient.setQueryData(
        datasetQueryKeys.mediaDetails(datasetId, {
          mediaId: context?.params.mediaId,
          modelId: currentModelId,
        }),
        context?.prevData,
      );
      enqueueSnackbar(
        t('Failed to save annotations for media {{name}}: {{errorMessage}}', {
          name: context?.params.mediaName,
          errorMessage: error.message,
        }),
        { variant: 'error' },
      );
    },
    onSettled: params => {
      if (projectId && datasetId && params) {
        queryClient.removeQueries(
          labelQueryKeys.modelAssistSuggestions(projectId, params.mediaId, currentModelId),
        );
        queryClient.invalidateQueries(datasetQueryKeys.allWithFilters(projectId));
      }
    },
  });
};

export const useBatchSetClass = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { datasetId, id: projectId } = useGetSelectedProjectQuery().data ?? {};
  const { id: currentModelId } = useCurrentProjectModelInfoQuery();

  return useMutation({
    mutationFn: async (params: { defectId: number; selectMediaOptions: SelectMediaOption }) => {
      if (!projectId || !datasetId) return;
      const result = await LabelAPI.batchSetClass({
        projectId,
        defectId: params.defectId,
        datasetId,
        selectMediaOptions: params.selectMediaOptions,
      });
      return { ...params, count: result.data.count };
    },
    onSuccess: (result, params) => {
      if (!datasetId || !projectId) return;

      // Invalidate all media details queries for affected media
      if (params?.selectMediaOptions.isUnselectMode) {
        queryClient.invalidateQueries(datasetQueryKeys.mediaDetails(datasetId));
      } else {
        params?.selectMediaOptions.selectedMedia?.forEach(mediaId => {
          queryClient.invalidateQueries(
            datasetQueryKeys.mediaDetails(datasetId, {
              mediaId,
              modelId: currentModelId,
            }),
          );
        });
      }

      // Invalidate dataset queries to refresh the data browser
      queryClient.invalidateQueries(datasetQueryKeys.allWithFilters(projectId));

      // Show success message
      enqueueSnackbar(
        t('Successfully set class for {{count}} media', {
          count: result?.count ?? 0,
        }),
        { variant: 'success' },
      );
    },
    onError: (error: Error) => {
      enqueueSnackbar(
        t('Failed to set class: {{errorMessage}}', {
          errorMessage: error.message,
        }),
        { variant: 'error' },
      );
    },
  });
};
